'use client';

import { useState } from 'react';
import { VStack, HStack } from '@terra/ui-styled-system';
import { BodyDefault, MetaDefault } from '../../../components/typography';
import { Button } from '../../../components/button';
import { Collapsible } from '../../../components/collapsible';
import { Scroll } from '../../Scroll';
import { Tabs } from '../../../components/tabs';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { InternalNetworkCopyableReadonlyField } from './InternalNetworkCopyableReadonlyField';
import { InternalNetworkSensitiveReadonlyField } from './InternalNetworkSensitiveReadonlyField';
import type { InternalNetworkEnrollmentSecrets } from './internal-connection.model';

export type InternalNetworkEnrollmentSecretsPanelProps = {
  secrets: InternalNetworkEnrollmentSecrets;
  maxHeight?: string;
};

export function InternalNetworkEnrollmentSecretsPanel({
  secrets,
  maxHeight = 'min(55vh, 520px)',
}: InternalNetworkEnrollmentSecretsPanelProps) {
  const tEnrollments = useTranslations(
    'designSystem.networkSettings.internalConnection.enrollments',
  );
  const tCommon = useTranslations('designSystem.common');
  const [installMode, setInstallMode] = useState<'quick' | 'secrets'>('quick');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  return (
    <Scroll
      orientation="vertical"
      showScrollbar
      maxH={maxHeight}
      w="full"
      minH={0}
    >
      <VStack gap={5} alignItems="stretch" w="full" pr={1}>
        <InternalNetworkSensitiveReadonlyField
          label={tEnrollments('apiKey')}
          value={secrets.apiKey}
          copyAriaLabel={tCommon('copy')}
          showAriaLabel={tEnrollments('showApiKey')}
          hideAriaLabel={tEnrollments('hideApiKey')}
        />

        <BodyDefault color="fg.muted">
          {tEnrollments('installOverview')}
        </BodyDefault>

        <Tabs.Root
          value={installMode}
          onValueChange={(details) => {
            if (details.value === 'quick' || details.value === 'secrets') {
              setInstallMode(details.value);
            }
          }}
          size="sm"
          w="full"
        >
          <Tabs.List>
            <Tabs.Trigger value="quick">
              {tEnrollments('installQuickTab')}
            </Tabs.Trigger>
            <Tabs.Trigger value="secrets">
              {tEnrollments('installSecretsTab')}
            </Tabs.Trigger>
            <Tabs.Indicator />
          </Tabs.List>

          <Tabs.Content value="quick" pt={4}>
            <VStack gap={4} alignItems="stretch">
              <MetaDefault color="fg.muted">
                {tEnrollments('installQuickDescription')}
              </MetaDefault>
              <InternalNetworkCopyableReadonlyField
                label={tEnrollments('composeYaml')}
                value={secrets.composeYaml}
                rows={10}
                minHeight="200px"
                copyAriaLabel={tCommon('copy')}
              />
              <InternalNetworkCopyableReadonlyField
                label={tEnrollments('deploymentCommand')}
                value={secrets.deploymentCommand}
                rows={6}
                minHeight="140px"
                copyAriaLabel={tCommon('copy')}
              />
            </VStack>
          </Tabs.Content>

          <Tabs.Content value="secrets" pt={4}>
            <VStack gap={4} alignItems="stretch">
              <MetaDefault color="fg.muted">
                {tEnrollments('installSecretsDescription')}
              </MetaDefault>
              <InternalNetworkCopyableReadonlyField
                label={tEnrollments('secretsComposeYaml')}
                value={secrets.secretsComposeYaml}
                rows={10}
                minHeight="200px"
                copyAriaLabel={tCommon('copy')}
              />
              <InternalNetworkCopyableReadonlyField
                label={tEnrollments('secretsDeploymentCommand')}
                value={secrets.secretsDeploymentCommand}
                rows={6}
                minHeight="140px"
                copyAriaLabel={tCommon('copy')}
              />
            </VStack>
          </Tabs.Content>
        </Tabs.Root>

        <Collapsible.Root
          open={advancedOpen}
          onOpenChange={({ open }) => setAdvancedOpen(open)}
        >
          <Collapsible.Trigger asChild>
            <Button
              variant="ghost"
              size="sm"
              justifyContent="flex-start"
              px={0}
            >
              <HStack gap={2}>
                {advancedOpen ? (
                  <ArrowDown01Icon size={14} />
                ) : (
                  <ArrowRight01Icon size={14} />
                )}
                <BodyDefault>
                  {tEnrollments('installAdvancedTitle')}
                </BodyDefault>
              </HStack>
            </Button>
          </Collapsible.Trigger>
          <Collapsible.Content>
            <VStack gap={4} alignItems="stretch" pt={3}>
              <MetaDefault color="fg.muted">
                {tEnrollments('installAdvancedDescription')}
              </MetaDefault>
              <InternalNetworkCopyableReadonlyField
                label={tEnrollments('pinnedImageReference')}
                value={secrets.pinnedImageReference}
                rows={2}
                copyAriaLabel={tCommon('copy')}
              />
              <InternalNetworkCopyableReadonlyField
                label={tEnrollments('imageVerifyScript')}
                value={secrets.imageVerifyScript}
                rows={8}
                minHeight="160px"
                copyAriaLabel={tCommon('copy')}
              />
              {secrets.cosignPublicKeyPem ? (
                <InternalNetworkCopyableReadonlyField
                  label={tEnrollments('cosignPublicKeyPem')}
                  value={secrets.cosignPublicKeyPem}
                  rows={6}
                  minHeight="120px"
                  copyAriaLabel={tCommon('copy')}
                />
              ) : null}
              <InternalNetworkCopyableReadonlyField
                label={tEnrollments('secretsSetupCommand')}
                value={secrets.secretsSetupCommand}
                rows={4}
                minHeight="100px"
                copyAriaLabel={tCommon('copy')}
              />
            </VStack>
          </Collapsible.Content>
        </Collapsible.Root>
      </VStack>
    </Scroll>
  );
}
